import Image from 'next/image'
import { useUserQuery } from 'auth/hooks'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { formatUserName } from 'utils/format-user-name'
import { IconShield } from '@repo/design-system-cf/components/icons'
import { Tooltip } from 'primitives/tooltip'

import { FollowButton } from './follow-button'
import { VerifiedPremiumRibbon } from '../../_components/verified-premium-ribbon'
import type { PublicUserProfile } from 'codegen/types'

interface ListUserType {
  id: string
  avatarUrl?: string | null
  displayName?: string | null
  firstName?: string | null
  lastName?: string | null
  slug?: string | null
  following?: boolean
  superSkill?: string | null
}

// oxlint-disable-next-line complexity
export const AvatarCard = ({
  member,
  isRealData = false,
  index,
  isCommunityLeaders = false,
  onFollowSuccess,
}: {
  member?: ListUserType
  isRealData?: boolean
  index?: number
  isCommunityLeaders?: boolean
  onFollowSuccess?: () => void
}) => {
  const { data: isOwner } = useUserQuery(undefined, {
    select: data => data?.me?.user?.id === member?.id,
  })

  if (!member) {
    return null
  }

  return (
    <>
      <div
        className="hidden sm:flex flex-col items-center justify-between bg-cf-white rounded-[12px] h-full border border-[#DADCE2] p-4"
        data-testid="clubhouses-user-avatar-card"
      >
        <div className="flex flex-col items-center overflow-hidden w-full justify-between h-full">
          <div className="flex flex-col items-center">
            <div className="h-[80px] w-[80px]">
              {member.avatarUrl ? (
                <Link
                  href={`${AppRoutes.SOCIAL_FEED_CRAFTER}/${member.slug}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Image
                    src={
                      isRealData
                        ? member.avatarUrl
                        : `https://avatar.iran.liara.run/public/${index}`
                    }
                    alt="logo"
                    width={80}
                    height={80}
                    className="overflow-hidden rounded-full border border-[#1313151F] object-cover"
                  />
                </Link>
              ) : null}
            </div>
            <div className="flex justify-center items-start w-full mt-2 px-1">
              <Link
                href={`${AppRoutes.SOCIAL_FEED_CRAFTER}/${member.slug}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-16 line-clamp-2 max-h-12 overflow-hidden text-ellipsis text-center font-medium hover:underline"
              >
                {formatUserName(member?.firstName, member?.lastName, 'User')}
              </Link>
              <div className="mt-[5px] mr-1">
                <VerifiedPremiumRibbon user={member as PublicUserProfile} />
              </div>
            </div>
            {member?.superSkill ? (
              <p className="text-[#4B5563] line-clamp-2 max-h-12 overflow-hidden text-ellipsis pt-1">
                {member.superSkill}
              </p>
            ) : (
              <div className="line-clamp-2 h-6 pt-1" />
            )}
          </div>
          {isCommunityLeaders ? (
            <Tooltip
              delay={0}
              value={
                <div className="relative max-w-[247px] py-2 bg-cf-white rounded-2 border border-[#0A0A0B1A] shadow-[0px_1px_3px_1px_rgba(0,0,0,0.1),_0px_1px_2px_0px_rgba(0,0,0,0.1)]">
                  <div className="absolute left-1/2 -top-[6px] -translate-x-1/2 flex justify-center w-0 h-0">
                    <div className=" w-0 h-0 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-b-[6px] border-[#0A0A0B1A]" />
                    <div className="absolute top-[2px] left-[-7px] w-0 h-0 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-b-[6px] border-b-cf-white" />
                  </div>
                  <p className="text-cf-sf-gray-1100 text-12">
                    {formatUserName(member?.firstName, member?.lastName, 'User')} manages the
                    members and posts for this Clubhouse.
                  </p>
                </div>
              }
              side="bottom"
              className="z-50 bg-transparent p-0"
              sideOffset={8}
              hideArrow
            >
              <div
                className="mt-4 w-full flex justify-center items-center gap-1 bg-cf-sky-blue-50 border border-[#1313151F] rounded-[24px] px-2"
                data-testid="clubhouses-community-leader-badge"
              >
                <IconShield className="size-3.5 text-cf-sky-blue-800" />
                <p className="text-cf-sky-blue-800 font-medium overflow-hidden inline-block max-w-fit text-ellipsis whitespace-nowrap truncate">
                  Community Leader
                </p>
              </div>
            </Tooltip>
          ) : null}
        </div>
        <div className="mt-4 w-full ">
          {!isOwner ? (
            <FollowButton
              following={member?.following}
              userId={member.id}
              className="w-full"
              onFollowSuccess={onFollowSuccess}
            />
          ) : (
            <div className="w-full h-9" />
          )}
        </div>
      </div>
      <div className="grid grid-cols-[80px,1fr] items-start gap-3 sm:hidden bg-cf-white rounded-[12px] border border-[#DADCE2] p-4">
        <div className="flex justify-center items-center h-full">
          {member.avatarUrl ? (
            <Link
              href={`${AppRoutes.SOCIAL_FEED_CRAFTER}/${member.slug}/`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                src={
                  isRealData ? member.avatarUrl : `https://avatar.iran.liara.run/public/${index}`
                }
                alt="logo"
                width={80}
                height={80}
                className="overflow-hidden rounded-full border border-[#1313151F] object-cover"
              />
            </Link>
          ) : null}
        </div>
        <div className="flex flex-col h-full justify-between">
          <div className="flex items-center">
            <Link
              href={`${AppRoutes.SOCIAL_FEED_CRAFTER}/${member.slug}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-16 line-clamp-2 max-h-12 overflow-hidden text-ellipsis font-medium hover:underline"
            >
              {formatUserName(member?.firstName, member?.lastName, 'User')}
            </Link>
            <VerifiedPremiumRibbon user={member as PublicUserProfile} />
          </div>
          {member?.superSkill ? (
            <p className="text-[#4B5563] line-clamp-2 max-h-12 overflow-hidden text-ellipsis pt-1">
              {member.superSkill}
            </p>
          ) : (
            <div className="line-clamp-2 h-6 overflow-hidden text-ellipsis pt-1" />
          )}
          {isCommunityLeaders ? (
            <div>
              <p className="mt-4 bg-cf-sky-blue-50 border border-[#1313151F] inline-block rounded-[24px] px-2 text-cf-sky-blue-800 font-medium">
                Community Leader
              </p>
            </div>
          ) : null}
          <div className="mt-4">
            {!isOwner ? (
              <FollowButton
                following={member?.following}
                userId={member.id}
                size="small"
                onFollowSuccess={onFollowSuccess}
              />
            ) : (
              <div className="h-[26px]" />
            )}
          </div>
        </div>
      </div>
    </>
  )
}
